import { Camera, Matrix4, Quaternion, Vector3 } from 'three';

/**
 * The orientation cube's sides by the direction of view that shows them: 'top' looks down,
 * 'right' looks west at the east side, 'back' looks south at the north side.
 */
export type CubeSide = 'top' | 'bottom' | 'right' | 'left' | 'back' | 'front';

type SideAxes = {
  /** Out of the side, in the world: x east, y north, z up. */
  normal: Vector3;
  /** Up on the side, as the camera that looks at it has it. */
  up: Vector3;
};

const sideAxes: Record<CubeSide, SideAxes> = {
  top: { normal: new Vector3(0, 0, 1), up: new Vector3(0, 1, 0) },
  bottom: { normal: new Vector3(0, 0, -1), up: new Vector3(0, 1, 0) },
  right: { normal: new Vector3(1, 0, 0), up: new Vector3(0, 0, 1) },
  left: { normal: new Vector3(-1, 0, 0), up: new Vector3(0, 0, 1) },
  back: { normal: new Vector3(0, 1, 0), up: new Vector3(0, 0, 1) },
  front: { normal: new Vector3(0, -1, 0), up: new Vector3(0, 0, 1) },
};

export const cubeSides = Object.keys(sideAxes) as CubeSide[];

/** The sides the cardinal directions turn the camera to. */
export const cardinalSides = { N: 'back', E: 'right', S: 'front', W: 'left' } as const;

export function getSideNormal(side: CubeSide) {
  return sideAxes[side].normal.clone();
}

/** The orientation of a camera looking straight at the side, from outside the cube. */
export function getSideViewQuaternion(side: CubeSide) {
  const { normal, up } = sideAxes[side];
  const lookAt = new Matrix4().lookAt(new Vector3(), normal.clone().negate(), up);
  return new Quaternion().setFromRotationMatrix(lookAt);
}

const toMatrix3d = (matrix: Matrix4) => `matrix3d(${matrix.elements.join(',')})`;

/**
 * Places an element, with its transform origin at its top left corner, in the cube's world frame:
 * its x along `right`, its y, down on screen, along `down`, its top left corner at `corner`.
 */
function placeElement(right: Vector3, down: Vector3, corner: Vector3) {
  // The screen's axes are left handed, x right, y down and z out of it, as the element's must be
  const out = new Vector3().crossVectors(down, right);
  return toMatrix3d(new Matrix4().makeBasis(right, down, out).setPosition(corner));
}

/** Places a side of the cube, `size` wide, on the cube around the origo. */
export function getSideTransform(side: CubeSide, size: number) {
  const { normal, up } = sideAxes[side];
  const right = new Vector3().crossVectors(up, normal);
  const corner = normal
    .clone()
    .multiplyScalar(size / 2)
    .addScaledVector(right, -size / 2)
    .addScaledVector(up, size / 2);
  return placeElement(right, up.clone().negate(), corner);
}

/**
 * Places one of the two crossed arrows of a side, pointing at its middle from `gap` outside it:
 * across the side's right or its up, so one of them shows whenever the side is seen edge on.
 */
export function getArrowTransform(
  side: CubeSide,
  across: 'right' | 'up',
  size: number,
  arrow: { width: number; gap: number },
) {
  const { normal, up } = sideAxes[side];
  const right = across === 'right' ? new Vector3().crossVectors(up, normal) : up.clone();
  const corner = normal
    .clone()
    .multiplyScalar(size / 2 + arrow.gap)
    .addScaledVector(right, -arrow.width / 2);
  return placeElement(right, normal, corner);
}

/**
 * Where a cardinal direction's letter goes on a ring around the cube, `distance` from its middle:
 * its middle on screen, in pixels from the cube's middle, y down. The letters are drawn flat on
 * the screen rather than in the cube's world, where they would be blurry, and could be seen edge on.
 */
export function getCardinalPosition(
  direction: keyof typeof cardinalSides,
  distance: number,
  camera: Camera,
) {
  const { normal } = sideAxes[cardinalSides[direction]];
  const { x, y } = normal.clone().multiplyScalar(distance).applyMatrix4(getCubeMatrix(camera));
  return { x, y };
}

/**
 * Turns the cube as the camera sees the world: the camera's view rotation, with y flipped, as y is
 * down on screen. No perspective: the cube looks the same from any distance.
 */
export function getCubeTransform(camera: Camera) {
  return toMatrix3d(getCubeMatrix(camera));
}

function getCubeMatrix(camera: Camera) {
  const view = new Matrix4().extractRotation(camera.matrixWorldInverse);
  return new Matrix4().makeScale(1, -1, 1).multiply(view);
}

/**
 * How much of a side's arrows shows: fully when the side is seen edge on, fading out as it turns
 * towards or away from the camera, so they're there for the sides that can't be clicked.
 */
export function getArrowOpacity(side: CubeSide, camera: Camera) {
  const direction = camera.getWorldDirection(new Vector3());
  return Math.max(0, 1 - Math.abs(direction.dot(sideAxes[side].normal)) * 2);
}
